"use client";

import React, { useMemo } from "react";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";

type SliderConfig = {
  label: string;
  prefix?: string;
  suffix?: string;
  min: number;
  max: number;
  step: number;
};

type SliderFieldProps = {
  config: SliderConfig;
  value: number;
  onChange: (value: number) => void;
};

export default function CalculatorSliderField({ config, value, onChange }: SliderFieldProps) {
  const clamp = (num: number): number => Math.min(config.max, Math.max(0, num));

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, "");
    const parsed = Number(raw);

    if (Number.isNaN(parsed)) return onChange(clamp(0));

    onChange(clamp(parsed));
  };

  const handleRangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  const percentage = useMemo(() => {
    return ((value - config.min) / (config.max - config.min)) * 100;
  }, [value, config.min, config.max]);

  const labelPositionClass = percentage < 25 ? "translate-x-0" : percentage > 75 ? "-translate-x-full" : "-translate-x-1/2";

  return (
    <div className="flex flex-col gap-2 sm:gap-4 w-full">
      <div className="text-2xs sm:text-base font-bold tracking-[1px] text-[#314158] uppercase">{config.label}</div>

      {/* Input */}
      <label className="border border-[#C6D3EA] rounded-xl px-4 py-2 sm:py-2.5 flex items-center gap-1.5 sm:gap-2 text-lg sm:text-xl leading-1.3 sm:leading-1.3 font-semibold text-primaryBlue">
        {config.prefix && <span className="opacity-50">{config.prefix}</span>}
        <input type="text" value={formatIndianNumber(value)} onChange={handleTextInput} className="w-full bg-transparent outline-hidden" inputMode="numeric" />
        {config.suffix && <span className="opacity-50">{config.suffix}</span>}
      </label>

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min={config.min}
          max={config.max}
          step={config.step}
          value={value}
          onChange={handleRangeInput}
          aria-label={config.label}
          style={{
            background: `linear-gradient(to right, #1a51aa 0%, #1a51aa ${percentage}%, #d4d4d4 ${percentage}%, #d4d4d4 100%)`,
          }}
          className="w-full h-1.5 rounded-lg appearance-none focus:outline-hidden
            [&::-webkit-slider-thumb]:appearance-none 
            [&::-webkit-slider-thumb]:w-4.5 
            [&::-webkit-slider-thumb]:h-4.5 
            xs:[&::-webkit-slider-thumb]:w-5.5 
            xs:[&::-webkit-slider-thumb]:h-5.5 
            [&::-webkit-slider-thumb]:bg-white 
            [&::-webkit-slider-thumb]:border-[3px] 
            xs:[&::-webkit-slider-thumb]:border-4 
            [&::-webkit-slider-thumb]:border-solid 
            [&::-webkit-slider-thumb]:border-primaryBlue 
            [&::-webkit-slider-thumb]:rounded-full 
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-moz-range-thumb]:appearance-none
            [&::-moz-range-thumb]:w-4.5 
            [&::-moz-range-thumb]:h-4.5 
            xs:[&::-moz-range-thumb]:w-5.5 
            xs:[&::-moz-range-thumb]:h-5.5 
            [&::-moz-range-thumb]:bg-white 
            [&::-moz-range-thumb]:border 
            [&::-moz-range-thumb]:border-primaryBlue 
            [&::-moz-range-thumb]:rounded-full 
            [&::-moz-range-thumb]:cursor-pointer"
        />

        {/* Floating Label */}
        <div className={`absolute -bottom-4.5 xs:-bottom-6 transition-transform duration-200 ${labelPositionClass} text-primaryBlue font-semibold text-xs sm:text-sm whitespace-nowrap`} style={{ left: `${percentage}%` }}>
          {config.prefix}
          {formatCompactNumber(value)}
        </div>
      </div>
    </div>
  );
}
